import { useQuery, UseQueryResult } from '@tanstack/react-query';
import { useSelector } from 'react-redux';
import { selectAuthToken } from 'src/store/selectors';
import { Study } from 'src/types/studies';
import {
  getStudies,
  getStudyById,
  getStudyOrganizations,
  getStudyParticipants,
  getStudyResearchers,
} from './studies';
import { configuration } from 'src/configuration';
import { StudyParticipant, StudyResearcher } from 'src/types/studies';

export function useGetStudies() {
  const token = useSelector(selectAuthToken);

  return useQuery<Study[]>({
    queryKey: ['studies'],
    queryFn: async () => {
      return (await getStudies({ token, search: '', after: '' })).studies;
    },
    staleTime: configuration.queriesStaleTime,
  });
}

export function useGetStudyById(studyId: string) {
  const token = useSelector(selectAuthToken);

  return useQuery({
    queryKey: ['study', studyId],
    queryFn: async () => {
      return (await getStudyById({ token, id: studyId })).study;
    },
    staleTime: configuration.queriesStaleTime,
  });
}

export function useGetStudyOrganizations(studyId: string) {
  const token = useSelector(selectAuthToken);

  return useQuery({
    queryKey: ['study-organizations', studyId],
    queryFn: async () => {
      const organizations = await getStudyOrganizations({ token, id: studyId });

      if (!organizations || organizations.organizations.length === 0) {
        throw new Error('Failed to fetch study organizations');
      }
      return organizations.organizations[0];
    },
    staleTime: configuration.queriesStaleTime,
  });
}

export function useGetStudyParticipants(
  studyId: string,
): UseQueryResult<{ participants: StudyParticipant[] }> {
  const token = useSelector(selectAuthToken);

  return useQuery({
    queryKey: ['study-participants', studyId],
    queryFn: async () => {
      return await getStudyParticipants({ token, id: studyId });
    },
    staleTime: configuration.queriesStaleTime,
  });
}

export function useGetStudyResearchers(
  studyId: string,
): UseQueryResult<{ researchers: StudyResearcher[] }> {
  const token = useSelector(selectAuthToken);

  return useQuery({
    queryKey: ['study-researchers', studyId],
    queryFn: async () => {
      return await getStudyResearchers({ token, id: studyId });
    },
    staleTime: configuration.queriesStaleTime,
  });
}
